import { computed, ref } from 'vue'
import api from '../services/api'
import { useAuthStore } from '../stores/auth'

const roleCache = new Map()
/** workspaceId -> criadorId (dono) para fallback antes/durante API */
const creatorIdByWorkspace = new Map()

export function setWorkspaceCreator(workspaceId, criadorId) {
  if (!workspaceId) return
  const key = String(workspaceId)
  if (criadorId == null || criadorId === '') {
    creatorIdByWorkspace.delete(key)
    return
  }
  creatorIdByWorkspace.set(key, Number(criadorId))
}

export function useWorkspaceAccess() {
  const authStore = useAuthStore()
  const roleWorkspace = ref('MEMBER')
  const loading = ref(false)
  const roleLoadedWorkspaceId = ref(null)

  const currentUserId = computed(() => {
    const id = authStore.user?.id
    return id != null ? Number(id) : null
  })

  const isWorkspaceCreator = (workspaceId) => {
    if (!workspaceId || currentUserId.value == null) return false
    const creatorId = creatorIdByWorkspace.get(String(workspaceId))
    return creatorId != null && creatorId === currentUserId.value
  }

  const isWorkspaceOwner = computed(() => roleWorkspace.value === 'OWNER')
  const isWorkspaceStaff = computed(() => roleWorkspace.value === 'ADMIN')
  const canManageWorkspaceSettings = computed(() => {
    const wsId = roleLoadedWorkspaceId.value
    if (wsId && isWorkspaceCreator(wsId)) return true
    return ['OWNER', 'ADMIN'].includes(roleWorkspace.value)
  })

  const isRoleLoadedFor = (workspaceId) =>
    roleLoadedWorkspaceId.value != null &&
    String(roleLoadedWorkspaceId.value) === String(workspaceId)

  const loadRoleForWorkspace = async (workspaceId, force = false) => {
    if (!workspaceId) return 'MEMBER'
    const key = String(workspaceId)

    if (isWorkspaceCreator(workspaceId)) {
      roleWorkspace.value = 'OWNER'
      roleLoadedWorkspaceId.value = workspaceId
      roleCache.set(key, 'OWNER')
      return 'OWNER'
    }

    if (!force && roleCache.has(key)) {
      roleWorkspace.value = roleCache.get(key)
      roleLoadedWorkspaceId.value = workspaceId
      return roleWorkspace.value
    }
    loading.value = true
    try {
      const { data } = await api.get(`/workspace-acesso/workspace/${workspaceId}/kanban-permissions/me`)
      let role = String(data?.roleWorkspace || 'MEMBER').toUpperCase()
      if (isWorkspaceCreator(workspaceId)) {
        role = 'OWNER'
      }
      roleWorkspace.value = role
      roleCache.set(key, role)
      roleLoadedWorkspaceId.value = workspaceId
      return role
    } catch {
      if (isWorkspaceCreator(workspaceId)) {
        roleWorkspace.value = 'OWNER'
        roleCache.set(key, 'OWNER')
      } else {
        roleWorkspace.value = 'MEMBER'
      }
      roleLoadedWorkspaceId.value = workspaceId
      return roleWorkspace.value
    } finally {
      loading.value = false
    }
  }

  const invalidateRole = (workspaceId) => {
    if (workspaceId) roleCache.delete(String(workspaceId))
  }

  return {
    roleWorkspace,
    loading,
    roleLoadedWorkspaceId,
    isWorkspaceOwner,
    isWorkspaceStaff,
    canManageWorkspaceSettings,
    isRoleLoadedFor,
    isWorkspaceCreator,
    loadRoleForWorkspace,
    invalidateRole
  }
}

export function clearWorkspaceRoleCaches() {
  roleCache.clear()
  creatorIdByWorkspace.clear()
}
